import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { ArrowRight, Eye, EyeOff, LockKeyhole, Mail } from "lucide-react";
import { useUser } from "./Context/UserContext.tsx";
import Maintopbar from "./maintopbar.tsx";
import PublicFooter from "./PublicFooter.tsx";
import "./CSS/PublicAuth.css";

const Login: React.FC = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const { setUser } = useUser();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const email = username.trim();
    if (!email || !password || submitting) return;
    setSubmitting(true);
    setErrorMessage("");
    try {
      const response = await axios.post(
        "https://timetrakerapi.sntservices.in/Login",
        { email, password }
      );
      const { id, firstName, email: accountEmail, isadmin, token } = response.data;
      localStorage.setItem("user", JSON.stringify({ id, firstName, email: accountEmail, isadmin }));
      localStorage.setItem("token", token);
      localStorage.setItem("userFirstName", firstName);
      localStorage.setItem("userEmail", accountEmail);
      setUser({ id, firstName, email: accountEmail, isadmin });
      toast.success("Welcome back!");
      navigate(isadmin ? "/admin-dashboard/activity-dashboard" : "/User-dashboard/UserprojectList");
    } catch (error: any) {
      const serverMessage = error.response?.data;
      setErrorMessage(typeof serverMessage === "string" ? serverMessage : serverMessage?.message || "Unable to sign in. Check your email and password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="public-auth-page">
      <Maintopbar />
      <main className="public-auth-main">
        <div className="public-auth-shell">
          <aside className="public-auth-story">
            <span className="public-auth-kicker">WELCOME BACK</span>
            <h1>Get back to the work that matters.</h1>
            <p>Sign in to see your team's activity, review timelines, and keep projects moving with a clearer view of time.</p>
            <div className="public-auth-story-card"><span className="public-auth-story-icon"><LockKeyhole size={21} /></span><div><strong>One workspace, every detail.</strong><small>Activity, projects, screenshots, and reports stay connected.</small></div></div>
            <div className="public-auth-story-decor" aria-hidden="true"><span /><span /><span /><span /><span /></div>
          </aside>
          <div className="public-auth-panel">
            <div className="public-auth-panel-head"><span className="public-auth-kicker">YOUR WORKSPACE</span><h2>Log in</h2><p>Enter your details to continue to Screenshot Monitor.</p></div>
            <form onSubmit={handleSubmit} className="public-auth-form">
              <div className="public-auth-field"><label htmlFor="login-email">Work email</label><div className="public-auth-input-wrap"><Mail size={18} /><input id="login-email" type="email" autoComplete="username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="you@company.com" required /></div></div>
              <div className="public-auth-field"><div className="public-auth-label-row"><label htmlFor="login-password">Password</label><Link to={`/forgotpassword?email=${encodeURIComponent(username.trim())}`}>Forgot password?</Link></div><div className="public-auth-input-wrap"><LockKeyhole size={18} /><input id="login-password" type={showPassword ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" required /><button type="button" className="public-auth-reveal" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? "Hide password" : "Show password"}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></div></div>
              {errorMessage && <p className="public-auth-error" role="alert">{errorMessage}</p>}
              <button type="submit" className="public-auth-submit" disabled={submitting}>{submitting ? "Signing in…" : <>Log in <ArrowRight size={18} /></>}</button>
            </form>
            <p className="public-auth-switch">New to Screenshot Monitor? <Link to="/signup">Create an account</Link></p>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
};

export default Login;
